import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import Formsy from 'formsy-react'

import { Button, IconButton, Table, TableContainer, TableHead, TableBody, Paper, 
         DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { Add, Delete, Edit } from '@mui/icons-material'

import _ from 'underscore'

import { C, D, Loading, SearchField, TRow, TCell, TextFieldFormsy, CustomSelect, ConfirmDelete } from '../include'

// ==========================================
// COMPONENTE: editar/adicionar departamentos
// ==========================================
const EditSection = ({ row, edit, api, alert, dialogClose }) => {
  const [courts, setCourts] = useState({});
  const [courtSelector, setCourtSelector] = useState(row ? [row.court] : []);
  const [court, setCourt] = useState(row ? row.court : '');
  const [section, setSection] = useState(row ? row.name : '');

  // Ref de proteção para o modal
  const isFirstRender = useRef(true);

  // Carrega as empresas/courts para o Select de forma blindada
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      api.get('/Courts').then(r => {
        setCourts(_.indexBy(r, 'nome'));
        setCourtSelector(Object.keys(_.groupBy(r, 'nome')));
      }).catch(e => console.error("Erro ao carregar courts", e));
    }
  }, [api]);

  const handleSave = () => {
    const payload = {
      court: {
        value: courts[court]?.id,
        label: court
      },
      name: section
    };

    if (!edit) {
      api.post('/Sections/', payload)
        .then(r => {
          dialogClose();
          alert(r.msg);
        })
        .catch(e => alert(e.msg || 'Erro ao salvar.'));
    } else {
      api.put(`/Section/${row.id}`, payload)
        .then(r => {
          dialogClose();
          alert(r.msg);
        })
        .catch(e => alert(e.msg || 'Erro ao atualizar.'));
    }
  };

  return (
    <Formsy onValidSubmit={() => null}>
      <DialogTitle style={{ minWidth: '400px' }}>
        {edit ? `Editar ${C.divisionLabel}` : `Adicionar ${C.divisionLabel}`}
      </DialogTitle>
      
      <DialogContent>
        <D h="1rem" />
        <CustomSelect 
          label={C.businessLabel} 
          options={courtSelector} 
          value={court} 
          onChange={v => setCourt(v)} 
          required 
          disabled={edit} 
        />
        <D h="1rem" />
        <TextFieldFormsy 
          value={section} 
          onChange={v => setSection(v)} 
          type="text" 
          name="path" 
          label={C.divisionLabel} 
          variant="outlined" 
          required 
        />
        <D h="2rem" />
      </DialogContent>
      
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={dialogClose}>
          Cancelar
        </Button>
        <Button variant="outlined" color="primary" onClick={handleSave}>
          Salvar
        </Button>
      </DialogActions>
    </Formsy>
  );
};


// ==========================================
// COMPONENTE PRINCIPAL: Lista de deparatmentos cadastrados
// ==========================================
export const Sections = ({ api, user, alert, dialog, dialogClose, permission }) => {
  const [searchField, setSearchField] = useState('');
  const [dataTable, setDataTable] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Ref de proteção contra o Strict Mode na tabela principal
  const isFirstRender = useRef(true);

  // Função estável na memória com useCallback
  const updateData = useCallback(() => {
    setIsLoading(true);
    api.get('/Sections')
      .then(r => {
        // Se for nao admin filtra, se for repassa o 'r' original intacto
        const finalData = user?.role === 'admin' 
          ? r
          : r.filter(item => !item.court?.includes('AI2')) ;

        setDataTable(finalData);
        setIsLoading(false);
      })
      .catch(() => {
        alert("Erro ao buscar dados.");
        setIsLoading(false);
      });
  }, [api, alert, user]);

  // Efeito blindado
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Tranca a porta instantaneamente
      updateData();
    }
  }, [updateData]);

  const openEditDialog = (row = null, isEdit = false) => {
    dialog(
      <EditSection 
        edit={isEdit} 
        row={row} 
        api={api} 
        alert={alert} 
        dialogClose={() => {
          dialogClose();
          updateData();
        }} 
      />
    );
  };

  const openDeleteDialog = (row) => {
    dialog(
      <ConfirmDelete
        msg="Tem certeza que deseja excluir o departamento:"
        itemName={row.name}
        onCancel={dialogClose}
        onConfirm={() => {
          api.delete(`/Section/${row.id}`)
            .then(r => {
              alert(r.msg);
              updateData(); 
              dialogClose();
            })
            .catch(e => alert(e.msg || 'Erro ao excluir o departamento.'));
        }}
      />
    );
  };

  // Otimização de filtro na memória
  const filteredData = useMemo(() => {
    if (!searchField) return dataTable;
    
    const searchLower = searchField.toLowerCase().replace(/\s/g, '');
    return dataTable.filter(row => {
      const rowValues = Object.values(row).map(v => String(v)).join('');
      return rowValues.toLowerCase().replace(/\s/g, '').includes(searchLower);
    });
  }, [dataTable, searchField]);

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      {permission === 'w' && (
        <>
          <D className="row w aF">
            <SearchField 
              value={searchField} 
              onChange={v => setSearchField(v)} 
            />
            <D e />
            <D size="2rem" />
            <Button
              variant="contained"
              color="primary"
              onClick={() => openEditDialog(null, false)}
            >
              <Add /> {C.divisionLabel}
            </Button>
          </D>
          <D h="1rem" />
        </>
      )}
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TRow sx={{ height: '3rem', background: '#ccc' }}>
                {permission === 'w' && <TCell align="center" sx={{ width: '8ch', maxWidth: '8ch' }}>Editar</TCell>}
                {permission === 'w' && <TCell align="center" sx={{ width: '8ch', maxWidth: '8ch' }}>Excluir</TCell>}

                <TCell sx={{ width: '20ch', maxWidth: '20ch', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {C.businessLabel}
                </TCell>

                <TCell sx={{ width: '25ch', maxWidth: '25ch', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Nome ({dataTable.length} departamentos)
                </TCell>

                {/* Coluna Buffer para absorver a sobra de espaço */}
                <TCell sx={{ p: 0, m: 0 }} />
              </TRow>
            </TableHead>
            
            <TableBody>
              {isLoading ? (
                <TRow>
                  <TCell component="th" align="center" colSpan={5}>
                    <Loading />
                  </TCell>
                </TRow>
              ) : filteredData.length > 0 ? (
                filteredData.map(row => (
                  <TRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    {permission === 'w' && (
                      <TCell align="center" component="th">
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => openEditDialog(row, true)}
                        >
                          <Edit />
                        </IconButton>
                      </TCell>
                    )}

                    {permission === 'w' && (
                      <TCell align="center" component="th">
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => openDeleteDialog(row)}
                        >
                          <Delete />
                        </IconButton>
                      </TCell>
                    )}
                    
                    <TCell component="th">{row.court}</TCell>
                    <TCell component="th">{row.name}</TCell>
                    <TCell /> {/* Célula Buffer */}
                  </TRow>
                ))
              ) : (
                <TRow>
                  <TCell component="th" align="center" colSpan={5}>
                    Nenhum departamento encontrado
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
};